// ── One statement record, viewed or edited ──────────────────────────────────
//
// Lives here rather than inside a page because two pages now show it: the
// Statements tab on an account, and the Publish Statements review page. A
// second copy would be a second place to add the next column to, and this app
// has already produced three separate bugs from one rule written down twice.
import { useState } from "react";
import { B } from "../../../shared/theme";
import { fmtDate } from "../../../shared/format";
import { Btn, Modal } from "../../../shared/ui";

// ── The generated date ─────────────────────────────────────────────────────
//
// A regenerated statement keeps its createdat — the MERGE updates in place and
// only the insert branch sets that column — so after a regenerate createdat
// names the day the period was FIRST written while the row points at a file
// produced today. As_of_Date already carries the period, so what this column is
// for is the produced date.
//
// COALESCE, not updatedat alone: a row migrated from the previous platform has
// never been regenerated here and has no updatedat, and its createdat IS its
// generation date.
export const generatedAt = (s) => s.updatedat || s.createdat;

// ── One statement, in full ─────────────────────────────────────────────────
//
// View and edit are the same modal because they are the same record; the only
// difference is whether the Note is a field or a line of text. Two components
// would be two places to add the next column to.
//
// ONLY THE NOTE IS EDITABLE, and that is a deliberate limit rather than an
// unfinished one. As_of_Date and Type are the key the batch MERGEs on
// (accountid, As_of_Date, Type) — edit either and the next regeneration stops
// finding this row, inserts a second one, and the account quietly has two
// statements for one period pointing at different files. Doc_Type and the Box
// fields describe a file this app did not put there and cannot move.
export const StatementModal = ({ statement: s, mode, onClose, onSaved, api }) => {
  const editing = mode === "edit";
  const [form, setForm] = useState({
    As_of_Date:  s.As_of_Date ? String(s.As_of_Date).slice(0, 10) : "",
    Type:        s.Type || "",
    Doc_Type:    s.Doc_Type || "",
    Box_URL:     s.Box_URL || "",
    Box_File_Id: s.Box_File_Id || "",
    Note:        s.Note || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // As_of_Date and Type are two thirds of the key the batch generators MERGE
  // on. Changing either means the next regeneration of this period will not
  // find this row — it inserts a second one instead, and the account ends up
  // with two statements for one period pointing at different files. That is
  // sometimes exactly what you want (a row filed under the wrong date), which
  // is why it is allowed; it is never what you want by accident, which is why
  // it is said out loud before saving.
  const keyChanged =
    form.As_of_Date !== (s.As_of_Date ? String(s.As_of_Date).slice(0, 10) : "")
    || form.Type !== (s.Type || "");

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post("updateStatement", {
        id: s.id,
        as_of_date:  form.As_of_Date,
        type:        form.Type,
        doc_type:    form.Doc_Type,
        box_url:     form.Box_URL,
        box_file_id: form.Box_File_Id,
        note:        form.Note,
      });
      onSaved(); onClose();
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const inputStyle = {
    width: "100%", boxSizing: "border-box", padding: "5px 8px", fontSize: 13,
    borderRadius: 4, border: "1px solid var(--color-border, #d5dbe2)",
    fontFamily: "var(--font-sans)",
  };

  const Row = ({ label, children }) => (
    <div style={{ display:"flex", gap:12, alignItems:"center", padding:"7px 0",
                  borderBottom:"0.5px solid var(--color-border-tertiary, #eee)" }}>
      <div style={{ flex:"0 0 150px", fontSize:12, fontWeight:600, color:"var(--color-text-secondary)" }}>{label}</div>
      <div style={{ flex:1, fontSize:13, wordBreak:"break-word" }}>{children}</div>
    </div>
  );

  // Both dates, but only when they differ — otherwise the modal asks the reader
  // to compare two identical values and work out why it bothered.
  const regenerated = s.updatedat && s.createdat
    && String(s.updatedat).slice(0, 10) !== String(s.createdat).slice(0, 10);

  return (
    <Modal title={(editing ? "Edit statement — " : "Statement — ") + (s.Type || "record")} onClose={onClose} wide={editing}>
      <Row label="As of Date">
        {editing
          ? <input type="date" value={form.As_of_Date} onChange={set("As_of_Date")} style={{ ...inputStyle, maxWidth:200 }} />
          : (s.As_of_Date ? fmtDate(s.As_of_Date) : "—")}
      </Row>
      <Row label="Type">
        {editing
          ? <>
              <input list="stmt-types" value={form.Type} onChange={set("Type")} style={inputStyle}
                     placeholder="Interest Statement" />
              <datalist id="stmt-types">
                <option value="Interest Statement" />
                <option value="Invoice (Estimate)" />
              </datalist>
            </>
          : (s.Type || "—")}
      </Row>
      <Row label="Doc Type">
        {editing
          ? <input value={form.Doc_Type} onChange={set("Doc_Type")} placeholder="xlsx"
                   style={{ ...inputStyle, maxWidth:200 }} />
          : <span style={{ textTransform:"uppercase" }}>{s.Doc_Type || "—"}</span>}
      </Row>
      <Row label="Status">
        <span style={{ fontSize:11, padding:"2px 9px", borderRadius:999, fontWeight:600,
          background: s.Publish ? "#E1F5EE" : "#FAECE7", color: s.Publish ? "#0F6E56" : "#993C1D" }}>
          {s.Publish ? "Published" : "Not Published"}
        </span>
        <span style={{ fontSize:11, color:"var(--color-text-secondary)", marginLeft:8 }}>
          {s.Publish ? "visible in the client portal" : "not visible to the borrower"}
          {editing ? " — change this from the Publish button on the list" : ""}
        </span>
      </Row>
      <Row label="Generated">
        {generatedAt(s) ? fmtDate(generatedAt(s)) : "—"}
        {editing && (
          <span style={{ fontSize:11, color:"var(--color-text-secondary)", marginLeft:8 }}>
            — set when the batch produces the file, not by an edit here
          </span>
        )}
      </Row>
      {regenerated && (
        <Row label="First created">
          {fmtDate(s.createdat)}
          <span style={{ fontSize:11, color:"var(--color-text-secondary)", marginLeft:8 }}>
            — regenerated since, so the file below is the newer one
          </span>
        </Row>
      )}
      <Row label="Box link">
        {editing
          ? <input value={form.Box_URL} onChange={set("Box_URL")} placeholder="https://app.box.com/file/…"
                   style={inputStyle} />
          : (s.Box_URL
              ? <a href={s.Box_URL} target="_blank" rel="noreferrer" style={{ color:B.cyan }}>Open in Box</a>
              : "—")}
      </Row>
      <Row label="Box file id">
        {editing
          ? <input value={form.Box_File_Id} onChange={set("Box_File_Id")} placeholder="2440294866839"
                   style={{ ...inputStyle, maxWidth:280 }} />
          : (s.Box_File_Id || "—")}
      </Row>
      <Row label="Note">
        {editing
          ? <textarea value={form.Note} onChange={set("Note")} rows={3} maxLength={2000}
              placeholder="Why this was regenerated, what to tell the borrower, anything worth keeping with the record."
              style={{ ...inputStyle, resize:"vertical" }} />
          : (s.Note || "—")}
      </Row>
      <Row label="Record id"><span style={{ fontSize:11, color:"var(--color-text-secondary)" }}>{s.id}</span></Row>

      {editing && keyChanged && (
        <div style={{ marginTop:12, padding:"10px 12px", borderRadius:6,
                      background:"#FAEEDA", borderLeft:"3px solid #BA7517",
                      fontSize:12, lineHeight:1.5, color:"#603700" }}>
          <strong>As of Date or Type has changed.</strong> The generators find an existing
          statement by account, as-of date and type. After this save, regenerating this
          period will no longer match this row — it will create a second one, and this one
          will stay behind pointing at the older file. That is the right move if you are
          correcting a misfiled record, and the wrong one if you only meant to fix a typo.
        </div>
      )}

      {err && <div style={{ color:"#993C1D", fontSize:12, marginTop:10 }}>{err}</div>}

      <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:16 }}>
        <Btn onClick={onClose}>{editing ? "Cancel" : "Close"}</Btn>
        {editing && <Btn variant="primary" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</Btn>}
      </div>
    </Modal>
  );
};

